const FEED_PREVIEW_CHARACTER_LIMIT = 160;

export const processHashtags = (html, mentions, surahLabel) => {
  if (!html) return html;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  tempDiv.querySelectorAll('span[data-mention], span.mention-mark').forEach((element) => {
    element.classList.add('mention-mark');
  });
  tempDiv.querySelectorAll('span[data-hashtag], span.hashtag-mark').forEach((element) => {
    element.classList.add('hashtag-mark');
    const hashtag = element.textContent.trim().replace(/^#/, '');
    if (hashtag) element.dataset.hashtag = hashtag;
  });
  tempDiv.querySelectorAll('a[href]').forEach((element) => {
    element.classList.add('post-content-link');
    element.setAttribute('rel', 'noopener noreferrer');
  });

  const processTextNodes = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      let text = node.textContent;

      const characterLinkRegex = /<(prophets|companions|historical):([^:>]+):([^>]+)>/g;
      text = text.replace(characterLinkRegex, (match, category, id, name) => {
        return `<span class="character-link-mark" data-category="${category}" data-id="${id}" style="color: rgb(29, 161, 242); font-weight: bold; cursor: pointer; background-color: rgba(29, 161, 242, 0.1); padding: 2px 6px; border-radius: 4px;">${name}</span>`;
      });

      const surahLinkRegex = /\^(\d+):([^\s<]+)/g;
      text = text.replace(surahLinkRegex, (match, number, name) => {
        return `<span class="surah-link-mark" data-number="${number}" style="color: rgb(29, 161, 242); font-weight: bold; cursor: pointer; background-color: rgba(29, 161, 242, 0.1); padding: 2px 6px; border-radius: 4px;">${surahLabel} ${name}</span>`;
      });

      const hashtagRegex = /(#[\u0600-\u06FF\w]+)/g;
      text = text.replace(hashtagRegex, (match, hashtag) => {
        const hashtagName = hashtag.substring(1);
        return `<span class="hashtag-mark" data-hashtag="${hashtagName}" style="color: rgb(29, 161, 242); font-weight: 500; cursor: pointer;">${hashtag}</span>`;
      });

      const mentionRegex = /(@[\u0600-\u06FF\w]+)/g;
      text = text.replace(mentionRegex, (match, mention) => {
        return `<span class="mention-mark" style="color: #1da1f2; font-weight: bold; cursor: pointer;">${mention}</span>`;
      });

      const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s<)]+)\)/g;
      text = text.replace(markdownLinkRegex, (match, text, url) => {
        return `<a href="${url}" style="color: #1da1f2; text-decoration: none;">${text}</a>`;
      });

      const urlRegex = /(https?:\/\/[^\s<]+)/g;
      text = text.replace(urlRegex, (match, url) => {
        return `<a href="${url}" style="color: #1da1f2; text-decoration: none;">${url}</a>`;
      });

      const wrapper = document.createElement('span');
      wrapper.innerHTML = text;
      node.parentNode.replaceChild(wrapper, node);
    } else if (
      node.nodeType === Node.ELEMENT_NODE
      && !['A', 'CODE', 'PRE'].includes(node.tagName)
      // Winner ranks such as #1 are display labels, not hashtags. Skip the
      // entire generated card so neither its rank nor its profile tag is
      // reinterpreted by the rich-text token pass.
      && !node.matches('.mention-mark, .hashtag-mark, .character-link-mark, .surah-link-mark, .embedded-winner-card, .event-inline-meta')
    ) {
      Array.from(node.childNodes).forEach(processTextNodes);
    }
  };

  Array.from(tempDiv.childNodes).forEach(processTextNodes);

  // Excerpt flattening and legacy posts may leave a visible @mention without
  // its data-id. Restore it from the structured mention list saved on posts.
  const mentionIds = new Map();
  (Array.isArray(mentions) ? mentions : []).forEach((mention) => {
    const handle = String(mention.username || mention.tag || '').replace(/^@/, '').trim();
    const id = mention.userId || mention.id;
    if (handle && id) mentionIds.set(handle.toLocaleLowerCase(), id);
  });
  tempDiv.querySelectorAll('.mention-mark').forEach((element) => {
    if (element.dataset.id) return;
    const handle = element.textContent.trim().replace(/^@/, '').toLocaleLowerCase();
    const id = mentionIds.get(handle);
    if (id) element.dataset.id = id;
  });

  return tempDiv.innerHTML;
};

export const getExcerpt = (html) => {
  if (!html) return '';

  // Keep the author's line breaks while bounding the preview's visible text.
  const source = document.createElement('div');
  const flattened = document.createElement('div');
  const syntheticBreaks = new WeakSet();
  const blockTags = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'LI', 'PRE']);
  source.innerHTML = html;

  const appendPlainContent = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      flattened.appendChild(document.createTextNode(node.textContent || ''));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE || ['SCRIPT', 'STYLE', 'IMG'].includes(node.tagName)) {
      return;
    }

    if (node.tagName === 'BR') {
      flattened.appendChild(document.createElement('br'));
      return;
    }

    if (node.tagName === 'A') {
      const label = (node.textContent || '').trim();
      const href = node.getAttribute('href') || '';
      if (label && /^(https?:\/\/|\/)/i.test(href)) {
        const link = document.createElement('a');
        link.href = href;
        link.textContent = label;
        flattened.appendChild(link);
      } else if (label) {
        flattened.appendChild(document.createTextNode(label));
      }
    } else {
      Array.from(node.childNodes).forEach(appendPlainContent);
    }

    if (blockTags.has(node.tagName)) {
      if (flattened.lastChild?.nodeName !== 'BR' || !node.childNodes.length) {
        const separator = document.createElement('br'); syntheticBreaks.add(separator); flattened.appendChild(separator);
      }
    }
  };

  Array.from(source.childNodes).forEach(appendPlainContent);
  flattened.querySelectorAll('*').forEach((element) => element.removeAttribute('style'));

  const textWalker = document.createTreeWalker(flattened, NodeFilter.SHOW_TEXT);
  let textNode = textWalker.nextNode();
  while (textNode) {
    textNode.textContent = textNode.textContent.replace(/\u00a0/g, ' ');
    textNode = textWalker.nextNode();
  }

  let remainingCharacters = FEED_PREVIEW_CHARACTER_LIMIT;
  const excerptTextNodes = [];
  const excerptWalker = document.createTreeWalker(flattened, NodeFilter.SHOW_TEXT);
  let excerptNode = excerptWalker.nextNode();
  while (excerptNode) {
    excerptTextNodes.push(excerptNode);
    excerptNode = excerptWalker.nextNode();
  }

  let cutoffNode = null;
  excerptTextNodes.forEach((node) => {
    if (remainingCharacters <= 0) {
      node.textContent = '';
      return;
    }
    // Tool payloads are atomic. Truncating a base64 countdown/linked-post token
    // exposes its internal ID instead of rendering the tool.
    const token = /\$(?:تنازلي|منشور|فائز)\s*\[[^\]]*\]|\$(?:منشور|فائز):[A-Za-z0-9_-]+/g;
    if (token.test(node.textContent)) {
      token.lastIndex = 0;
      let text = '', cursor = 0;
      for (const match of node.textContent.matchAll(token)) {
        const prefix = node.textContent.slice(cursor, match.index).slice(0, remainingCharacters);
        text += prefix; remainingCharacters -= prefix.length;
        if (remainingCharacters > 0) { text += match[0]; remainingCharacters = Math.max(0, remainingCharacters - 24); }
        cursor = match.index + match[0].length;
      }
      const tail = node.textContent.slice(cursor).slice(0, remainingCharacters);
      text += tail; remainingCharacters -= tail.length; node.textContent = text;
      if (!remainingCharacters && !cutoffNode) cutoffNode = node;
      return;
    }
    if (node.textContent.length <= remainingCharacters) {
      remainingCharacters -= node.textContent.length;
      if (!remainingCharacters && !cutoffNode) cutoffNode = node;
      return;
    }

    let shortenedText = node.textContent.slice(0, remainingCharacters);
    const lastWordBoundary = shortenedText.lastIndexOf(' ');
    if (lastWordBoundary > Math.floor(remainingCharacters * 0.65)) {
      shortenedText = shortenedText.slice(0, lastWordBoundary);
    }
    node.textContent = shortenedText.trimEnd();
    remainingCharacters = 0;
    if (!cutoffNode) cutoffNode = node;
  });

  if (cutoffNode) {
    while (cutoffNode.parentNode !== flattened) cutoffNode = cutoffNode.parentNode;
    while (cutoffNode.nextSibling) cutoffNode.nextSibling.remove();
  }

  // Remove only the synthetic closing block separator; authored blank lines
  // and hard breaks within the preview stay intact.
  if (syntheticBreaks.has(flattened.lastChild)) flattened.lastChild.remove();
  return flattened.innerHTML.trim();
};

